import React, {
  forwardRef,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import RemoveIcon from '@material-ui/icons/RemoveCircleOutline';
import { Button } from '@workboard/wobo-ui';
import { KrMilestoneContextValue } from '@workboard/ui-okr';
import { type KrMilestonesProps } from '../../../KrMilestones';
import KrMilestones from '../../../KrMilestones';
import HelpTextComp from '../../../helper/helptext.component';
import { METRIC_SOURCE } from '../../../../okr/constants/okrPopUp.constants';
import { useClosedKrLock } from '../closedKrLock.context';

const KrMilestonesSection = forwardRef(
  (props: KrMilestonesProps, ref: React.Ref<KrMilestoneContextValue>) => {
    const { t } = useTranslation();
    const isClosedKr = useClosedKrLock();
    const {
      metric,
      goalDetails,
      metricState,
      onChange,
    } = props;
    const [showMilestones, setShowMilestones] = useState(!!metric?.milestones?.length);
    const source =
      props.source ??
      metricState?.source ??
      metricState?.krObj?.source ??
      metricState?.metricObj?.source ??
      metricState?.metricObject?.source ??
      metric?.source;
    const counting =
      metricState?.dsMetric ??
      metricState?.metricObject?.counting ??
      metric?.counting;
    const isIncreaseOrDecreaseToTarget = counting == 0 || counting == 1 || counting == 2;
    const isWorkstreamMetric = source == String(METRIC_SOURCE.SOURCE_WORKSTREAM);
    const isMirrorKrMetric = source == String(METRIC_SOURCE.SOURCE_MIRROR_KR);
    const isMITKrMetric = props.mitChildKr || props.isChildKr;
    const isValidKrType =
      isIncreaseOrDecreaseToTarget &&
      !isWorkstreamMetric &&
      !isMirrorKrMetric &&
      !isMITKrMetric;

    const handleRemoveMilestones = () => {
      setShowMilestones(false);
      onChange?.([]);
    };

    const handleAddMilestones = () => {
      setShowMilestones(true);
    };

    useEffect(() => {
      if (!isValidKrType) {
        setShowMilestones(false);
        onChange?.([]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValidKrType]);

    const isMilestonesEnabled = window?.wbConfig?.isMilestonesEnabled;
    if (!isMilestonesEnabled || !isValidKrType) return null;

    return (
      <div className="kr-milestones-wrapper" style={{ padding: '12px 0px' }}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Button
            style={{
              padding: '4px 4px 4px 0px',
              fontSize: '13px',
              fontWeight: 700,
            }}
            kind="normal"
            onClick={() => (showMilestones ? handleRemoveMilestones() : handleAddMilestones())}
            variant="ghost"
            isDisabled={isClosedKr}
            className="krAlign-label"
            startIcon={
              showMilestones ? (
                <RemoveIcon
                  style={{
                    cursor: 'pointer',
                    width: '18px',
                    color: '#596373',
                    fontWeight: 700,
                    margin: '0px 0px 0px 4px',
                  }}
                />
              ) : (
                <AddIcon
                  style={{
                    cursor: 'pointer',
                    width: '18px',
                    color: '#596373',
                    fontWeight: 700,
                    margin: '0px 0px 0px 4px',
                  }}
                />
              )
            }
          >
            {showMilestones ? t('Remove Milestones') : t('Milestones')}
          </Button>
          <HelpTextComp key="ml-help-text" wrapperClass="committedKRHelpText marginL3">
            {t('Define intermediate targets to see if your KR is on track relative to your planned trajectory.')}
          </HelpTextComp>
        </div>
        {showMilestones && (
          <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '20px' }}>
            <div>
              <div
                id="edit-kr-milestones-area"
                style={{
                  marginLeft: '12px',
                  color: 'rgb(89, 99, 115)',
                  fontWeight: 700,
                  fontSize: '14px',
                  width: '100%',
                }}
                className="edit-kr-milestones-area"
              >
                <KrMilestones
                  ref={ref}
                  metric={metric}
                  goalDetails={goalDetails}
                  metricState={metricState}
                  kr_duration={props.kr_duration}
                  disabled={isClosedKr}
                  onChange={onChange}
                />
              </div>
            </div>
            <div className="krBreak" aria-hidden="true" />
          </div>
        )}
      </div>
    );
  }
);

export default KrMilestonesSection;
